import { theme } from '@lmc/ui-kit'
import { Select } from '@lmc/ui-kit/src/dataEntry'
import { Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { type FC, type FocusEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FormItem } from '../Form/FormItem'
import { ClockIcon } from './ClockIcon'
import {
  containsLetters,
  convertMinutes,
  generateTimeOptions,
  getisValidTime,
  getTimeDifference
} from './utils'

dayjs.extend(duration)

export const DEFAULT_TIME_START = '08:00'

export const DEFAULT_TIME_END = '08:30'

export const STEP_MINUTES = 10

export const MINUTES_PER_DAY = 1440

const MIN_DIGITS_FOR_NORMALIZATION = 3
const MAX_HOURS = 23
const MAX_MINUTES = 59
const TIME_FORMAT_LENGTH = 5

/**
 * Validates if a number is a valid hour (0-23)
 */
const isValidHour = (hour: number): boolean => {
  return !isNaN(hour) && hour >= 0 && hour <= MAX_HOURS
}

/**
 * Validates if a number is a valid minute (0-59)
 */
const isValidMinute = (minute: number): boolean => {
  return !isNaN(minute) && minute >= 0 && minute <= MAX_MINUTES
}

/**
 * Formats a number as a two-digit string with leading zero
 */
const formatTwoDigits = (num: number): string => {
  return num.toString().padStart(2, '0')
}

/**
 * Normalizes partial time input to HH:MM format.
 * Only normalizes when input is complete enough to avoid interfering with typing.
 *
 * @param input - Raw time input string (e.g., "9:45", "945", "12:4")
 * @returns Normalized time string in HH:MM format, or null if normalization should be skipped
 */
const normalizeTimeInput = (input: string): string | null => {
  if (!input) return null

  const cleaned = input.replace(/[^\d:]/g, '')

  if (cleaned.includes(':')) {
    return normalizeTimeWithColon(cleaned)
  }

  return normalizeTimeWithoutColon(cleaned)
}

/**
 * Normalizes time input that contains a colon (e.g., "9:45", "12:4")
 */
const normalizeTimeWithColon = (cleaned: string): string | null => {
  const [hoursStr = '', minutesStr = ''] = cleaned.split(':')
  const digitCount = (hoursStr + minutesStr).replace(/\D/g, '').length

  // Don't normalize if too few digits or minutes are incomplete
  if (digitCount < MIN_DIGITS_FOR_NORMALIZATION || minutesStr.length === 1) {
    return null
  }

  // Don't normalize if already in correct format (hours start with 0)
  if (hoursStr.startsWith('0') && hoursStr.length > 1) {
    return null
  }

  const hoursNum = parseInt(hoursStr, 10)
  const minutesNum = minutesStr ? parseInt(minutesStr, 10) : 0

  if (!isValidHour(hoursNum)) return null
  if (minutesStr && !isValidMinute(minutesNum)) return null

  const formattedHours = formatTwoDigits(hoursNum)
  const formattedMinutes = minutesStr
    ? minutesStr.substring(0, 2).padStart(2, '0')
    : '00'

  return `${formattedHours}:${formattedMinutes}`
}

/**
 * Normalizes time input without colon (e.g., "945", "1234")
 */
const normalizeTimeWithoutColon = (cleaned: string): string | null => {
  const digits = cleaned.replace(/\D/g, '')

  if (digits.length === 0 || digits.length < MIN_DIGITS_FOR_NORMALIZATION) {
    return null
  }

  // Don't normalize if starts with 0 (to avoid premature normalization)
  if (digits.startsWith('0')) {
    return null
  }

  const hoursStr = digits.substring(0, digits.length - 2)
  const minutesStr = digits.substring(digits.length - 2)

  const hoursNum = parseInt(hoursStr, 10)
  const minutesNum = parseInt(minutesStr, 10)

  if (!isValidHour(hoursNum) || !isValidMinute(minutesNum)) {
    return null
  }

  return `${formatTwoDigits(hoursNum)}:${formatTwoDigits(minutesNum)}`
}

type Props = {
  startTime?: string
  endTime?: string
  onStartTimeChanged?: (time?: string) => void
  onEndTimeChanged?: (time?: string) => void
  disableStartTime?: boolean
  disableEndTime?: boolean
  disableTooltip?: boolean
  'data-testid'?: string
}

export const TimeInput: FC<Props> = ({
  startTime,
  onStartTimeChanged,
  onEndTimeChanged,
  disableStartTime,
  disableEndTime,
  disableTooltip,
  'data-testid': dataTestId
}) => {
  const [opened, setOpened] = useState(false)

  const [valueSearchStart, setValueSearchStart] = useState<string>()

  const [valueSearchEnd, setValueSearchEnd] = useState<string>()

  const optionsStart = generateTimeOptions(
    valueSearchStart && valueSearchStart?.length > 4 ? 1 : STEP_MINUTES
  ).reverse()

  const optionsEnd = generateTimeOptions(
    valueSearchEnd && valueSearchEnd?.length > 4 ? 1 : STEP_MINUTES
  ).reverse()

  /**
   * Handles search input for time fields.
   * Always sets raw input first to allow editing, then normalizes if appropriate.
   */
  const handleTimeSearch = (
    value: string,
    setSearchValue: (value: string) => void
  ) => {
    if (containsLetters(value)) return

    // Always set raw input first to allow editing (including backspace)
    setSearchValue(value)

    // If already in valid format, don't normalize
    if (value?.length === TIME_FORMAT_LENGTH && value.includes(':')) {
      return
    }

    // Normalize partial input if appropriate
    const normalized = normalizeTimeInput(value)
    if (normalized) {
      setSearchValue(normalized)
    }
  }

  const handleSearchStart = (e: string) => {
    handleTimeSearch(e, setValueSearchStart)
  }

  const handleSearchEnd = (e: string) => {
    handleTimeSearch(e, setValueSearchEnd)
  }

  const handleChangeStart = (newValue: unknown) => {
    const timeValue = newValue as string
    if (getisValidTime(timeValue)) {
      onStartTimeChanged?.(timeValue)
    }
  }

  const handleChangeEnd = (newValue: unknown) => {
    const timeValue = newValue as string
    if (getisValidTime(timeValue)) {
      onEndTimeChanged?.(timeValue)
    }
  }

  return (
    <Wrapper>
      <Tooltip
        color={theme.color.neutral.color3}
        title={
          disableStartTime ? (
            <ContentContainer>
              <Text>{disableTooltip}</Text>
            </ContentContainer>
          ) : (
            ''
          )
        }
        open={disableStartTime ? undefined : false}
        overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
      >
        <FormItem
          name="startTime"
          rules={[
            {
              required: true,
              message: `The Start time field is required`
            }
          ]}
        >
          <Select
            data-testid={dataTestId ? `${dataTestId}-start` : undefined}
            options={optionsStart}
            showSearch
            allowClear
            disabled={disableStartTime}
            onSearch={handleSearchStart}
            searchValue={valueSearchStart}
            placeholder="HH:MM"
            suffixIcon={
              <IconWrapper>
                <ClockIcon />
              </IconWrapper>
            }
            onChange={handleChangeStart}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              const value = e?.target?.value
              if (getisValidTime(value)) {
                handleChangeStart(value)
              }
            }}
            notFoundContent="Not a valid time"
            onClear={() => onStartTimeChanged?.(undefined)}
            autoClearSearchValue={false}
          />
        </FormItem>
      </Tooltip>
      <Tooltip
        color={theme.color.neutral.color3}
        title={
          disableEndTime ? (
            <ContentContainer>
              <Text>{disableTooltip}</Text>
            </ContentContainer>
          ) : (
            ''
          )
        }
        open={disableEndTime ? undefined : false}
        overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
      >
        <FormItem
          name="endTime"
          rules={[
            {
              required: true,
              message: `The End time field is required`
            }
          ]}
        >
          <Select
            data-testid={dataTestId ? `${dataTestId}-end` : undefined}
            options={optionsEnd}
            showSearch
            allowClear
            disabled={disableEndTime}
            onFocus={() => setOpened(true)}
            onDropdownVisibleChange={(open: boolean) => setOpened(open)}
            onSelect={(value: any) => {
              const timeValue = String(value || '')
              if (getisValidTime(timeValue)) {
                setOpened(false)
                handleChangeEnd(timeValue)
              }
            }}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              const value = e?.target?.value
              if (getisValidTime(value)) {
                setOpened(false)
                handleChangeEnd(value)
              }
            }}
            onChange={handleChangeEnd}
            optionRender={(option: any) => {
              const start = valueSearchStart || startTime
              const optionLabel = String(option.label || '')
              const difference = start
                ? getTimeDifference(start, optionLabel)
                : null
              const isPast = difference !== null && Number(difference) < 0
              const minutes =
                isPast && difference !== null
                  ? MINUTES_PER_DAY - Number(difference) * -1
                  : difference

              const formatDuration = (totalMinutes: number | null): string => {
                if (totalMinutes === null || totalMinutes === 0) {
                  return '24h'
                }
                const { hours, minutes: mins } = convertMinutes(totalMinutes)
                if (hours === 0 && mins === 0) {
                  return '24h'
                }
                return `(${hours}h ${mins}m)`
              }

              const shouldShowDuration = opened && difference !== null
              const startEndMatch = start === option.value

              return (
                <OptionWrapper>
                  {optionLabel}
                  {shouldShowDuration && (
                    <TimeWrapper>
                      {formatDuration(minutes as number)}
                    </TimeWrapper>
                  )}
                  {opened && !difference && startEndMatch && (
                    <TimeWrapper>24h</TimeWrapper>
                  )}
                </OptionWrapper>
              )
            }}
            onSearch={handleSearchEnd}
            searchValue={valueSearchEnd}
            placeholder="HH:MM"
            suffixIcon={
              <IconWrapper>
                <ClockIcon />
              </IconWrapper>
            }
            notFoundContent="Not a valid time"
            onClear={() => onEndTimeChanged?.(undefined)}
            autoClearSearchValue={false}
          />
        </FormItem>
      </Tooltip>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
`

const OptionWrapper = styled.div`
  display: flex;
`

const TimeWrapper = styled.div`
  display: flex;
  margin-left: 5px;
  color: ${(props) => props.theme.color.neutral.color5};
`

const IconWrapper = styled.div`
  margin-right: -2px;
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color11};
`

export default TimeInput
